import styled from '@emotion/styled';
import { css, type SerializedStyles, type Theme } from '@emotion/react';

import { Flex } from '../Flex';
import { GridItem } from './GridItem';

import type { BreakpointUnit, SpacingUnit } from '@toolbox/style';
import type { CSSInterpolation } from '@emotion/serialize';

/**
 * Makes an object based on breakpoints specified as following:
 * {
 *   xsGap?: SpacingUnit;
 *   smGap?: SpacingUnit;
 *   mdGap?: SpacingUnit;
 *   ...
 * }
 */
type GapProps = { [key in `${BreakpointUnit}Gap`]?: Spacing };

type Spacing = SpacingUnit | [SpacingUnit, SpacingUnit];

export type GridProps = GapProps & {
  preset?: boolean;
};

export const Grid = styled(Flex)<GridProps>`
  ${({ preset, theme }) =>
    preset &&
    css`
      ${spacingStyles(theme)(2, GridItem)}

      ${theme.media.up('md')} {
        ${spacingStyles(theme)(3, GridItem)}
      }
    `}

  ${({ theme, ...props }) =>
    Object.keys(theme.breakpoints).map((bpu) => {
      const bp = bpu as BreakpointUnit;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const bpGap = props[`${bp}Gap` as keyof GapProps] as SpacingUnit;
      return (
        bpGap != undefined &&
        (bp === 'xs'
          ? spacingStyles(theme)(bpGap, GridItem)
          : css`
              ${theme.media.up(bp)} {
                ${spacingStyles(theme)(bpGap, GridItem)}
              }
            `)
      );
    })}
`;

Grid.defaultProps = {
  preset: true,
  wrap: 'wrap',
};

function spacingStyles(theme: Theme) {
  return (gap: Spacing, childSelector: CSSInterpolation): SerializedStyles => {
    const [vGap, hGap] = getSpacing(gap, theme);

    return css`
      width: calc(100% + 2 * ${hGap});
      margin: -${vGap} -${hGap};

      & > ${childSelector} {
        padding: ${vGap} ${hGap};
      }
    `;
  };
}

function getSpacing(gap: Spacing, theme: Theme): [string, string] {
  const gaps = Array.isArray(gap) ? gap : [gap, gap];
  return gaps.map((gap) => theme.spacing(gap)) as [string, string];
}
